import { omit } from "lodash-es";

import type {
  FieldChange as ApiFieldChange,
  GetStickiesResponse,
  PartialSticky as ApiPartialSticky,
  Sticky as ApiSticky,
  StickyChange as ApiStickyChange,
  StickyUpdate as ApiStickyUpdate,
  User as ApiUser,
} from "@/backend/natsstubs/board/models";
import { keysToCamel } from "@/backend/natsstubs/wire/transform";
import { withExplicitDefaults } from "@/backend/read/changeSticky";

import type {
  ServerFieldChange,
  ServerStickiesUpdate,
  ServerSticky,
  ServerStickyChange,
  ServerStickyUpdate,
  ServerUser,
} from "../serverModel";

/**
 * Maps a generated board `Sticky` (camelCase, from the NATS `board.state.get`
 * stub) to the legacy snake_case `ServerSticky` the existing callers expect.
 *
 * The server serializes with exclude_none, so on a full read an absent field
 * means "cleared" — the result runs through `withExplicitDefaults`, which
 * restores the legacy null floor for every clearable field. Required fields
 * the stub leaves optional (`text`, `iteration_number`, `a_center`,
 * `stack_pos`) get the same fallbacks the legacy backend produced. The delta
 * mapper below must do NEITHER: there, absence means "unchanged".
 */
export function mapApiStateSticky(sticky: ApiSticky): ServerSticky {
  return withExplicitDefaults({
    id: sticky.id,
    board_id: sticky.boardId,
    type_id: sticky.typeId,
    text: sticky.text ?? "",
    iteration_number: sticky.iterationNumber ?? null,
    a_center: sticky.aCenter ?? [0.5, 0.5],
    stack_pos: sticky.stackPos ?? 0,
    ...(sticky.stickyGroup !== undefined && { sticky_group: sticky.stickyGroup }),
    ...(sticky.almIssueId !== undefined && { alm_issue_id: sticky.almIssueId }),
    ...(sticky.almIssueUrl !== undefined && { alm_issue_url: sticky.almIssueUrl }),
    ...(sticky.almSource !== undefined && { alm_source: sticky.almSource }),
    ...(sticky.storyPoints !== undefined && { story_points: sticky.storyPoints }),
    ...(sticky.WSJFValue !== undefined && { WSJF_value: sticky.WSJFValue }),
    ...(sticky.stepValue !== undefined && { step_value: sticky.stepValue }),
    ...(sticky.teamId !== undefined && { team_id: sticky.teamId }),
    ...(sticky.precondTeam !== undefined && { precond_team: sticky.precondTeam }),
    ...(sticky.precondTeamId !== undefined && { precond_team_id: sticky.precondTeamId }),
    ...(sticky.dependTeam !== undefined && { depend_team: sticky.dependTeam }),
    ...(sticky.dependTeamId !== undefined && { depend_team_id: sticky.dependTeamId }),
    ...(sticky.flagType !== undefined && { flag_type: sticky.flagType }),
    ...(sticky.risk !== undefined && { risk: sticky.risk }),
    ...(sticky.status !== undefined && { status: sticky.status }),
    ...(sticky.statusClass !== undefined && { status_class: sticky.statusClass }),
    ...(sticky.transition !== undefined && { transition: sticky.transition }),
    ...(sticky.artId !== undefined && { art_id: sticky.artId }),
    ...(sticky.objectives !== undefined && {
      objectives: sticky.objectives.map((o) => ({ id: o.id, board_id: o.boardId })),
    }),
    ...(sticky.reactions !== undefined && { reactions: sticky.reactions }),
    ...(sticky.assignee !== undefined && { assignee: mapApiUser(sticky.assignee) }),
    ...(sticky.reporter !== undefined && { reporter: mapApiUser(sticky.reporter) }),
  });
}

function mapApiUser(user: ApiUser | null): ServerUser | null {
  return user ? { pip_id: user.pipId, alm_id: user.almId } : null;
}

export function mapApiStickyChange(change: ApiStickyChange): ServerStickyChange {
  const base = {
    sticky_id: change.stickyId,
    timestamp: change.timestamp,
    user_id: change.userId,
  };
  switch (change.kind) {
    case "create":
      return { ...base, kind: change.kind };
    case "update":
    case "delete":
      return { ...base, kind: change.kind, changes: change.changes.map(mapApiFieldChange) };
    case "mirror":
    case "unmirror":
      return {
        ...base,
        kind: change.kind,
        from_board: change.fromBoard,
        to_board: change.toBoard,
      };
    case "link":
    case "unlink":
      return { ...base, kind: change.kind, linked_to: change.linkedTo };
  }
}

function mapApiFieldChange(fieldChange: ApiFieldChange): ServerFieldChange {
  return {
    key: fieldChange.key as ServerFieldChange["key"],
    old: fieldChange.old ?? null,
    new: fieldChange.new ?? null,
  };
}

/**
 * The write requests carry the fields `toServerSticky` emits, in the stubs' casing. Server-owned
 * fields never travel on a write, and the few the read model types wider than the request model
 * (a cleared team has no name/id on the wire, art ids are numbers) are narrowed to what is sent.
 */
export function toApiPartialSticky(sticky: Partial<ServerSticky>): ApiPartialSticky {
  const {
    a_center,
    status,
    precond_team,
    precond_team_id,
    depend_team,
    depend_team_id,
    art_id,
    ...rest
  } = omit(sticky, ["linked_stickies", "status_distribution"]);
  return {
    ...keysToCamel(rest),
    ...(a_center && { aCenter: a_center }),
    ...(present(status) && { status }),
    ...(present(precond_team) && { precondTeam: precond_team }),
    ...(present(precond_team_id) && { precondTeamId: precond_team_id }),
    ...(present(depend_team) && { dependTeam: depend_team }),
    ...(present(depend_team_id) && { dependTeamId: depend_team_id }),
    ...(art_id !== undefined && { artId: art_id }),
  };
}

function present<T>(value: T | null | undefined): value is T {
  return value !== undefined && value !== null;
}

/** `stickies.get` carries the enrichments `state.get` never has. */
export function mapApiSticky(sticky: ApiSticky): ServerSticky {
  return {
    ...mapApiStateSticky(sticky),
    ...(sticky.linkedStickies !== undefined && {
      linked_stickies: sticky.linkedStickies.map((linked) => ({
        link_id: linked.linkId,
        sticky_id: linked.stickyId,
        direction: linked.direction,
      })),
    }),
    ...(sticky.statusDistribution !== undefined && {
      status_distribution: sticky.statusDistribution,
    }),
    status_distribution_state: sticky.statusDistributionState,
  };
}

/**
 * The legacy delta contract of sticky.list: a plain request answers the list, a request with
 * the previous fetch's timestamp answers the updates since then or falls back to the full list.
 */
export function mapApiStickiesResponse(
  response: GetStickiesResponse,
): ServerSticky[] | ServerStickiesUpdate {
  if (response.timestamp === undefined) {
    return requireStickies(response).map(mapApiSticky);
  }
  if (response.updates !== undefined) {
    return { timestamp: response.timestamp, updates: response.updates.map(mapApiStickyUpdate) };
  }
  return { timestamp: response.timestamp, stickies: requireStickies(response).map(mapApiSticky) };
}

/** An empty board answers an empty list; a response with NEITHER list is a
 * broken server answer — treating it as empty would delete every card. */
function requireStickies(response: GetStickiesResponse): ApiSticky[] {
  if (response.stickies === undefined) {
    throw new Error("stickies.get answered neither stickies nor updates");
  }
  return response.stickies;
}

function mapApiStickyUpdate(update: ApiStickyUpdate): ServerStickyUpdate {
  // an update's obj carries only the changed fields (a delete just the id);
  // it stays partial so changeSticky does not mistake defaults for edits
  return { event: update.event, obj: mapApiPartialSticky(update.obj) };
}

/** Maps only the fields the delta actually carries — no fallbacks, no
 * cleared-null restoration; absence means "unchanged" here. */
function mapApiPartialSticky(obj: ApiPartialSticky): ServerStickyUpdate["obj"] {
  if (obj.id === undefined) {
    // the server stamps the stored _id into every update object
    throw new Error("sticky update without an id");
  }
  return {
    id: obj.id,
    ...(obj.boardId !== undefined && { board_id: obj.boardId }),
    ...(obj.typeId !== undefined && { type_id: obj.typeId }),
    ...(obj.text !== undefined && { text: obj.text }),
    ...(obj.iterationNumber !== undefined && { iteration_number: obj.iterationNumber }),
    ...(obj.aCenter !== undefined && { a_center: obj.aCenter }),
    ...(obj.stackPos !== undefined && { stack_pos: obj.stackPos }),
    ...(obj.stickyGroup !== undefined && { sticky_group: obj.stickyGroup }),
    ...(obj.almIssueId !== undefined && { alm_issue_id: obj.almIssueId }),
    ...(obj.almIssueUrl !== undefined && { alm_issue_url: obj.almIssueUrl }),
    ...(obj.almSource !== undefined && { alm_source: obj.almSource }),
    ...(obj.storyPoints !== undefined && { story_points: obj.storyPoints }),
    ...(obj.WSJFValue !== undefined && { WSJF_value: obj.WSJFValue }),
    ...(obj.stepValue !== undefined && { step_value: obj.stepValue }),
    ...(obj.teamId !== undefined && { team_id: obj.teamId }),
    ...(obj.precondTeam !== undefined && { precond_team: obj.precondTeam }),
    ...(obj.precondTeamId !== undefined && { precond_team_id: obj.precondTeamId }),
    ...(obj.dependTeam !== undefined && { depend_team: obj.dependTeam }),
    ...(obj.dependTeamId !== undefined && { depend_team_id: obj.dependTeamId }),
    ...(obj.flagType !== undefined && { flag_type: obj.flagType }),
    ...(obj.risk !== undefined && { risk: obj.risk }),
    ...(obj.status !== undefined && { status: obj.status }),
    ...(obj.statusClass !== undefined && { status_class: obj.statusClass }),
    ...(obj.transition !== undefined && { transition: obj.transition }),
    ...(obj.artId !== undefined && { art_id: obj.artId }),
    ...(obj.objectives !== undefined && {
      objectives: obj.objectives.map((o) => ({ id: o.id, board_id: o.boardId })),
    }),
    ...(obj.reactions !== undefined && { reactions: obj.reactions }),
    ...(obj.linkedStickies !== undefined && {
      linked_stickies: obj.linkedStickies.map((linked) => ({
        link_id: linked.linkId,
        sticky_id: linked.stickyId,
        direction: linked.direction,
      })),
    }),
    ...(obj.statusDistribution !== undefined && {
      status_distribution: obj.statusDistribution,
    }),
    status_distribution_state: obj.statusDistributionState,
    ...(obj.assignee !== undefined &&
      obj.assignee !== null && { assignee: mapApiUser(obj.assignee) }),
    ...(obj.reporter !== undefined &&
      obj.reporter !== null && { reporter: mapApiUser(obj.reporter) }),
  };
}
